import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import AdvisoryEditor, {
  type AdvisoryEditorSubmit,
} from "../components/AdvisoryEditor";
import LoadingSpinner from "../components/LoadingSpinner";
import { apiService, extractApiError } from "../services/api";
import type { SecurityAdvisory } from "../types";

export default function EditSecurityAdvisoryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: response,
    isLoading,
    error: loadError,
  } = useQuery(
    ["security-advisory", id],
    () => apiService.getSecurityAdvisory(id!),
    { enabled: !!id },
  );

  const advisory = response?.data?.data as SecurityAdvisory | undefined;

  const updateMutation = useMutation(
    (payload: AdvisoryEditorSubmit) =>
      apiService.updateSecurityAdvisory(id!, {
        title: payload.title,
        status: payload.status,
        csaf: payload.csaf,
      }),
    {
      onSuccess: () => {
        toast.success("Security advisory updated");
        queryClient.invalidateQueries(["security-advisories"]);
        queryClient.invalidateQueries(["security-advisory", id]);
        navigate("/security-advisories");
      },
      onError: (error: unknown) => {
        toast.error(
          extractApiError(error, "Failed to update security advisory"),
        );
      },
    },
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const header = (subtitle: string) => (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => navigate("/security-advisories")}
        className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Edit advisory
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
      </div>
    </div>
  );

  if (loadError || !advisory) {
    return (
      <div className="space-y-6">
        {header("Advisory details")}
        <div className="card p-6 text-sm text-red-600 dark:text-red-400">
          This security advisory could not be found.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header(advisory.title)}

      <AdvisoryEditor
        mode="edit"
        initialCsaf={advisory.csaf as Record<string, unknown>}
        initialBusinessArea={advisory.businessArea}
        initialDivision={advisory.division}
        submitting={updateMutation.isLoading}
        onSubmit={(payload) => updateMutation.mutate(payload)}
      />
    </div>
  );
}
